import QueryString from 'qs';
import { match, P } from 'ts-pattern';
import { TableSortData } from 'src/components/general/table-helpers';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useTableLayoutQueryParams } from 'src/hooks/use-table-layout-query-params';

const QUERY_PAGE_SIZE = 50;

const resolveNoFilters = () => ({});

export type AiAgentsListVariables<OrderBy> = {
  companyId: number;
  orderBy: OrderBy;
  first?: number;
  after?: string;
  last?: number;
  before?: string;
};

export const useAiAgentsListQueryParams = <
  OrderBy,
  Filters extends object = Record<string, never>,
  ExtraVariables extends object = Record<string, never>,
>(props: {
  defaultSortData: TableSortData;
  resolveOrderBy: (sortData: TableSortData) => OrderBy;
  filtersResolver?: (params: QueryString.ParsedQs) => Filters;
  resolveExtraVariables?: (filters: Filters) => ExtraVariables;
}) => {
  const {
    defaultSortData,
    resolveOrderBy,
    filtersResolver = resolveNoFilters as () => Filters,
    resolveExtraVariables,
  } = props;
  const { id: companyId } = useCurrentInstitution();

  return useTableLayoutQueryParams<
    AiAgentsListVariables<OrderBy> & Partial<ExtraVariables>,
    Filters
  >({
    defaultSortData,
    variablesResolver: (params, sortData) => {
      const paginationVariables = match({ before: params.before, after: params.after })
        .with({ before: P.nonNullable }, ({ before }) => ({
          before,
          last: QUERY_PAGE_SIZE,
        }))
        .with({ after: P.nonNullable }, ({ after }) => ({
          after,
          first: QUERY_PAGE_SIZE,
        }))
        .otherwise(() => ({ first: QUERY_PAGE_SIZE }));
      const extraVariables: Partial<ExtraVariables> = resolveExtraVariables
        ? resolveExtraVariables(filtersResolver(params))
        : {};

      return {
        ...paginationVariables,
        ...extraVariables,
        companyId,
        orderBy: resolveOrderBy(sortData),
      };
    },
    filtersResolver,
  });
};
